import store from "../store/store";
import App from "../components/app";
import {i18nService} from "../services/i18n-service";

interface ProductValues {
  id: string;
  brand: string;
  country: string;
  maintenance_group: string;
  variants: VariantValues[];
  web_category: string;
  customer_group: string;
  descriptions: DescriptionValues[];
}

export class Product {
  public id: string;
  public brand: string;
  public country: string;
  public maintenance_group: string;
  public variants: Variant[];
  public web_category: string;
  public customer_group: string;
  public descriptions: Description[];

  constructor(values: ProductValues) {
    this.brand = values.brand;
    this.country = values.country;
    this.maintenance_group = values.maintenance_group;
    let variants: Variant[] = [];
    if (values.variants) {
      for (let valueVariant of values.variants) {
        if (valueVariant) {
          variants.push(new Variant(valueVariant))
        }
      }
    }
    this.variants = variants;

    let descriptions: Description[] = [];
    if (values.descriptions) {

      for (let description of values.descriptions) {
        descriptions.push(new Description(description))
      }
    }
    this.descriptions = descriptions;

    this.web_category = values.web_category;
    this.customer_group = values.customer_group;
  }

  getVariant(productId: string, variantId: string): Variant {
    return this.variants.find(variant => {
      return variant.productId === productId && variant.id === variantId;
    });
  }

  getMatchingVariant(): Variant {
    const state = store.getState();

    // early exit if there is only one variant
    if (this.variants.length === 1) {
      return this.variants[0];
    }

    let remainingVariants: Variant[] = this.variants;
    //check coming-soon-filter
    const comingSoonFilter = state.filters.selected.find(it => it.type == FilterType.Custom && it.name == 'coming_soon');
    if (comingSoonFilter) {
      const comingSoonVariants = remainingVariants.filter(variant => variant.comingSoon);
      if (comingSoonVariants.length > 0) {
        remainingVariants = comingSoonVariants
      }
    }

    //check sale-filter
    const saleFilter = state.filters.selected.find(it => it.type == FilterType.Custom && it.name == "sale");
    if (saleFilter) {
      const saleVariants = remainingVariants.filter(variant => variant.sale);
      if (saleVariants.length > 0) {
        remainingVariants = saleVariants
      }
    }

    //check new-in-filter
    const newInFilter = state.filters.selected.find(it => it.type == FilterType.Custom && it.name == "new_in");
    if (newInFilter) {
      const newInVariants = remainingVariants.filter(variant => variant.newIn);
      if (newInVariants.length > 0) {
        remainingVariants = newInVariants
      }
    }

    //check highlight filter
    const highlightFilter = state.filters.selected.find(it => it.type == FilterType.Custom && it.name == 'highlight');
    if (highlightFilter) {
      const highlightVariants = remainingVariants.filter(variant => variant.highlight);
      if (highlightVariants.length > 0) {
        remainingVariants = highlightVariants
      }
    }

    //check color-filter
    const colorFilters = state.filters.selected.filter(it => it.type == FilterType.Color);
    if (colorFilters && colorFilters.length > 0) {
      const colorVariants = remainingVariants.filter(variant => {
        return colorFilters.find(filter => variant.basicColor === filter.name)
      });
      if (colorVariants.length > 0) {
        remainingVariants = colorVariants
      }
    }

    /* find by order 1) Likes 2) NEW IN 3) Non-Sale (with Outfit-Image) 4) Non-Sale 5) Rest */

    // find first liked
    for (const variant of remainingVariants) {
      for (let like of state.likes) {
        if (variant.productId === like.productId && variant.id === like.variantId) {
          return variant;
        }
      }
    }

    // find first new in
    for (const variant of remainingVariants) {
      if (variant.newIn) {
        return variant;
      }
    }

    // find first highlight
    for (const variant of remainingVariants) {
      if (variant.highlight) {
        return variant;
      }
    }

    // find first non-sale variant with an outfit image
    for (const variant of remainingVariants) {
      if (!variant.sale) {
        for (const image of variant.images) {
          if (image.type === 'OUTFIT_IMAGE') {
            return variant;
          }
        }
      }
    }

    // find first non-sale and not new in => "normal" variants
    for (const variant of remainingVariants) {
      if (!variant.sale) {
        return variant;
      }
    }

    // if no variant was found until now return the first one
    return remainingVariants[0];
  }

  public hasAdditionalColors() {
    return this.variants.length >= 2;
  }

  public getAdditionalColorsCount() {
    return this.hasAdditionalColors() ? this.variants.length - 1 : 0;
  }

}

type DescriptionValues = {
  description: string;
  language: string;
}

export class Description {

  public description: string;
  public language: string;

  constructor(values: DescriptionValues ) {
    this.description = values.description;
    this.language = values.language;
  }
}

interface SizeValues {
  size_value: string;
  size_name: string;
  bar_code: string;
}

export class Size {
  public sizeValue: string;
  public sizeName: string;
  public barCode: string;

  constructor(values: SizeValues) {
    this.sizeValue = values.size_value;
    this.sizeName = values.size_name;
    this.barCode = values.bar_code;
  }
}

export type ImageType = 'CUTOUT' | 'OUTFIT_VIDEO' | 'OUTFIT_IMAGE'

interface ImageValues {
  angle: string;
  key: string;
  has_thumbnail?: boolean;
  hasThumbnail?: boolean;
  type?: ImageType;
  position: number;
}

export class Image {
  public angle: string;
  public key: string;
  public hasThumbnail: boolean;
  public type: ImageType;
  public position: number;

  constructor(values: ImageValues) {
    this.angle = values.angle;
    this.key = values.key;
    this.hasThumbnail = values.hasThumbnail ? values.hasThumbnail : values.has_thumbnail ? values.has_thumbnail : false;
    this.type = values.type ? values.type : 'CUTOUT';
    this.position = values.position;
  }
}

interface ComponentValues {
  id: string;
  main: boolean;
  lining: boolean;
  materials: MaterialValues[];
}

export class Components {
  public id: string;
  public main: boolean;
  public lining: boolean;
  public materials: Material[];

  constructor(values: ComponentValues) {
    this.id = values.id;
    this.main = values.main;
    this.lining = values.lining;

    this.materials = [];
    if(values.materials) {
      for(let material of values.materials) {
        this.materials.push(new Material(material));
      }
    }
  }

  translationKey(): string {
    return `comp_${this.id}`
  }

  clone() {
    return new Components({
      id: this.id,
      main: this.main,
      lining: this.lining,
      materials: this.materials.map(mat => ({
        id: mat.id,
        metal: mat.metal,
        recycled: mat.recycled,
        microplastic: mat.microplastic,
        value: mat.value
      }))
    })
  }
}

interface MaterialValues {
  id: string;
  metal: boolean;
  recycled: boolean;
  microplastic: boolean;
  value: number;
}

export class Material {
  public id: string;
  public metal: boolean;
  public recycled: boolean;
  public microplastic: boolean;
  public value: number;

  constructor(values: MaterialValues) {
    this.id = values.id;
    this.metal = values.metal;
    this.recycled = values.recycled;
    this.microplastic = values.microplastic;
    this.value = values.value;
  }

  translationKey(): string {
    return `mat_${this.id}`
  }
}

export const ImageAngleSort = {};
ImageAngleSort["KEY_VISUAL"] = 1;
ImageAngleSort["FRONT"] = 2;
ImageAngleSort["LEFT"] = 3;
ImageAngleSort["RIGHT"] = 4;
ImageAngleSort["BACK"] = 5;
ImageAngleSort["INSIDE"] = 6;
ImageAngleSort["BOTTOM"] = 7;
ImageAngleSort["TOP"] = 8;
ImageAngleSort["DETAIL"] = 9;

export const ImageTypeSort = {};
ImageTypeSort["OUTFIT_IMAGE"] = 1;
ImageTypeSort["CUTOUT"] = 2;
ImageTypeSort["OUTFIT_VIDEO"] = 3;

export function sortByPositionAndTypeAndAngle(images: Image[]): Image[] {
  if (!images || images.length <= 0) {
    return images;
  }

  images.sort((a, b) => {
    if ('position' in a && 'position' in b && a.position !== b.position && a.type !== 'OUTFIT_VIDEO' && b.type !== 'OUTFIT_VIDEO') {
      return a.position - b.position
    } else {
      // sort all key visual types first, then by angle and than by the rest of the types
      const typeSort1 = ImageTypeSort[a.type];
      const typeSort2 = ImageTypeSort[b.type];
      const angleSort1 = ImageAngleSort[a.angle];
      const angleSort2 = ImageAngleSort[b.angle];
      return typeKeyVisual(a) - typeKeyVisual(b) || typeSort1 - typeSort2 || angleSort1 - angleSort2;
    }
  });

  return images;
}

function typeKeyVisual(image: Image) {
  return image.angle === 'KEY_VISUAL' ? 1 : 2;
}

interface VariantValues {
  id: string;
  product_id?: string;
  productId?: string;
  currency: string;
  images: ImageValues[];
  current_price?: number;
  currentPrice?: number;
  original_price?: number;
  originalPrice?: number;
  pantone_color?: string;
  pantoneColor?: string;
  red_price_change?: boolean;
  redPriceChange?: boolean;
  coming_soon?: boolean;
  comingSoon?: boolean;
  new_in?: boolean;
  newIn?: boolean;
  highlight?: boolean;
  sale?: boolean;
  sizes: SizeValues[];
  basic_color?: string;
  basicColor?: string;
  color_group?: string;
  colorGroup?: string;
  red: number;
  green: number;
  blue: number;
  color_name?: string;
  colorName?: string;
  publish_date?: Date;
  publishDate?: Date;
  contains_high_microplastic: boolean;
  contains_recycled_material: boolean;
  fabrication_country: string;
  enhancement_country: string;
  assembling_country: string;
  dangerous_substance: boolean;
  shooting_model_size: string;
  shooting_product_size: string;
  components?: ComponentValues[];
  exchange_currency?: string;
  exchangeCurrency?: string;
  current_exchange_price?: number;
  currentExchangePrice?: number;
  original_exchange_price?: number;
  originalExchangePrice?: number;
}

export class Variant {

  public id: string;
  public productId: string;
  public currency: string;
  public images: Image[];
  public currentPrice: number;
  public originalPrice: number;
  public pantoneColor: string;
  public redPriceChange: boolean;
  public comingSoon: boolean;
  public newIn: boolean;
  public highlight: boolean;
  public sizes: Size[];
  public basicColor: string;
  public colorGroup: string;
  public sale: boolean;
  public red: number;
  public green: number;
  public blue: number;
  public colorName: string;
  public publishDate: Date;
  public contains_high_microplastic: boolean;
  public contains_recycled_material: boolean;
  public fabrication_country: string;
  public enhancement_country: string;
  public assembling_country: string;
  public dangerous_substance: boolean;
  public shooting_model_size: string;
  public shooting_product_size: string;
  public components: Components[];
  public exchangeCurrency: string | null;
  public originalExchangePrice: number | null;
  public currentExchangePrice: number | null;

  constructor(values: VariantValues) {
    this.basicColor = values.basic_color ? values.basic_color : values.basicColor;
    this.blue = values.blue;
    this.colorGroup = values.color_group ? values.color_group : values.colorGroup;
    this.colorName = values.color_name ? values.color_name : values.colorName;
    this.currency = values.currency;
    this.currentPrice = values.current_price ? values.current_price : values.currentPrice;
    this.green = values.green;
    this.comingSoon = values.coming_soon ? values.coming_soon : values.comingSoon;
    this.newIn = values.new_in ? values.new_in : values.newIn;
    this.highlight = values.highlight ? values.highlight : false;
    this.sale = values.sale ? values.sale : false;
    this.contains_high_microplastic = values.contains_high_microplastic;
    this.contains_recycled_material = values.contains_recycled_material;
    this.fabrication_country = values.fabrication_country;
    this.enhancement_country = values.enhancement_country;
    this.assembling_country = values.assembling_country;
    this.dangerous_substance = values.dangerous_substance;
    this.shooting_model_size = values.shooting_model_size;
    this.shooting_product_size = values.shooting_product_size;
    this.exchangeCurrency = values.exchange_currency ? values.exchange_currency : values.exchangeCurrency;
    this.currentExchangePrice = values.current_exchange_price ? values.current_exchange_price : values.currentExchangePrice;

    this.images = [];
    for (let image of values.images) {
      this.images.push(new Image(image))
    }

    this.images = sortByPositionAndTypeAndAngle(this.images);

    this.originalPrice = values.original_price ? values.original_price : values.originalPrice;
    this.originalExchangePrice = values.original_exchange_price ? values.original_exchange_price : values.originalExchangePrice;
    this.pantoneColor = values.pantone_color ? values.pantone_color : values.pantoneColor;
    this.publishDate = values.publish_date ? values.publish_date : values.publishDate;
    this.red = values.red;
    if (this.originalPrice <= this.currentPrice) {
      this.redPriceChange = false
    } else {
      this.redPriceChange = values.red_price_change ? values.red_price_change : values.redPriceChange;
    }

    this.sizes = [];
    for (let size of values.sizes) {
      this.sizes.push(new Size(size))
    }

    this.id = values.id;
    this.productId = values.product_id ? values.product_id : values.productId;

    this.components = []
    if(values.components) {
      for(let component of values.components) {
        this.components.push(new Components(component))
      }
    }
  }

  public hasTag(): boolean {
    return this.comingSoon || this.sale || this.newIn || this.highlight;
  }

  public showPriceLabel(): boolean {
    return !this.comingSoon;
  }

  public getColorTranslation() {
    let key = (this.colorGroup ? `colorgroup_${this.colorGroup}` : `filter_color_${this.basicColor}`).toLowerCase();
    return i18nService.translate(key);
  }

  public hasAllCountryData() {
    return !!(this.enhancement_country && this.assembling_country && this.fabrication_country);
  }
}

export function formatPrice(price: number, currency: string) {
  return price?.toLocaleString(App.countryCode, {
    style: "currency",
    currency: currency,
    currencyDisplay: 'code'
  }) ?? undefined;
}

export enum Gender {
  None = "NONE",
  Male = "MALE",
  Female = "FEMALE",
}

export function GenderFromString(input: string): Gender {
  if (input) {
    switch (input.toUpperCase()) {
      case Gender.None:
        return Gender.None;
      case Gender.Male:
        return Gender.Male;
    }
  }
  return Gender.Female
}

export enum FilterType {
  Custom = "CUSTOM",
  Brand = "BRAND",
  Category = "CATEGORY",
  Color = "COLOR",
  Like = "LIKE",
  Collection = "COLLECTION",
  Editorial = "EDITORIAL",
}
